import { ZoneType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { addZoneType } from "@/lib/api/zone"
import { useToast } from "@/hooks/use-toast"

import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form"
import { Input } from "../ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  zone_name: z.string().min(1, "Zone name is required"),
  zone_code: z.string().min(1, "Zone code is required"),
  meter_reading_schedule: z.coerce
    .number({
      required_error: "Meter reading schedule is required",
      invalid_type_error: "Meter reading schedule must be a number",
    })
    .min(1, { message: "Meter reading schedule must be between 1 and 31" })
    .max(31, { message: "Meter reading schedule must be between 1 and 31" }),
  zone_description: z.string().min(1, "Zone description is required"),
  zone_capacity: z.coerce
    .number()
    .min(0, "Zone capacity must be a positive number"),
  zone_status: z.string().min(1, "Zone status is required"),
  meter_count: z.coerce
    .number()
    .min(0, "Meter count must be a positive number"),

  before_due_reminder: z.coerce
    .number()
    .min(0, "Before due reminder must be a positive number")
    .max(31, "Before due reminder must be less than or equal to 31"),
  overdue_reminder: z.coerce
    .number()
    .min(0, "Overdue reminder must be a positive number")
    .max(31, "Overdue reminder must be less than or equal to 31"),
  disconnection_reminder: z.coerce
    .number()
    .min(0, "Disconnection reminder must be a positive number")
    .max(31, "Disconnection reminder must be less than or equal to 31"),
})

export default function ZonesForm({
  setShowAddZoneTypeModal,
}: {
  setShowAddZoneTypeModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const days = Array.from({ length: 31 }, (_, i) => i + 1)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_name: "",
      zone_code: "",
      meter_reading_schedule: 0,
      zone_description: "",
      zone_capacity: 0,
      zone_status: "",
      meter_count: 1,
      before_due_reminder: 1,
      overdue_reminder: 1,
      disconnection_reminder: 1,
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ZoneType) => {
      await addZoneType(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zonesDetails"] })
      toast({
        description: "Zone Type added successfully",
      })
      setShowAddZoneTypeModal(false)
    },
    onError: async (error: any) => {
      let errorMessage = "Please try again."

      // Check if error itself has a message or error property
      if (error && typeof error === "object") {
        if ("error" in error) {
          errorMessage = (error as any).error
        } else if ("message" in error) {
          errorMessage = (error as any).message
        } else {
          errorMessage = JSON.stringify(error)
        }
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as ZoneType)
  }
  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            {/* Zone Name and Zone Code */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="zone_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Code <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Meter Reading Schedule */}
            <FormField
              control={form.control}
              name="meter_reading_schedule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Meter Reading Schedule{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Zone Description */}
            <FormField
              control={form.control}
              name="zone_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Zone Description <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Zone Capacity and Zone Status */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mb-3">
              <FormField
                control={form.control}
                name="zone_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Capacity <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Status <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="before_due_reminder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Before Due Reminder{" "}
                      <span className="text-destructive">*</span>
                    </FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select day (1–31)" />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        {days.map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            Day {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <FormDescription>
                      Day of the month to send reminder
                    </FormDescription>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="overdue_reminder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Overdue Reminder{" "}
                      <span className="text-destructive">*</span>
                    </FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select day (1–31)" />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        {days.map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            Day {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <FormDescription>
                      Day of the month to send reminder
                    </FormDescription>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="disconnection_reminder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Disconnection Reminder{" "}
                      <span className="text-destructive">*</span>
                    </FormLabel>

                    <Select
                      onValueChange={field.onChange}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select day (1–31)" />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        {days.map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            Day {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <FormDescription>
                      Day of the month to send reminder
                    </FormDescription>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
          <div className="flex flex-row justify-end gap-2">
            <Button
              className="w-28"
              variant="outline"
              type="button"
              onClick={() => {
                setShowAddZoneTypeModal(false)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
